/**
 * /erp/orders — list of orders (ERP v1.2).
 *
 * Status filter + free-text search across code, account, and contact.
 * Each row links to the detail page where the lifecycle buttons live.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Filter, Loader2, PackageCheck, Plus, Search, ShoppingCart } from "lucide-react";
import { useMemo, useState } from "react";

import { type ErpOrderStatus, erpApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { useOrders } from "../lib/erp";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/erp/orders/")({
  component: OrderListPage,
});

const STATUSES: { id: ErpOrderStatus | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "pending_production", label: "Pending production" },
  { id: "ready_for_dispatch", label: "Ready" },
  { id: "partially_fulfilled", label: "Partial" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

export const ORDER_STATUS_STYLES: Record<ErpOrderStatus, string> = {
  draft: "border-[var(--border)] opacity-70",
  pending_production:
    "border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40",
  ready_for_dispatch:
    "border-blue-400 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40",
  partially_fulfilled:
    "border-indigo-400 text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40",
  completed: "border-green-400 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/40",
  cancelled: "border-red-400 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40",
};

export function orderStatusLabel(status: ErpOrderStatus): string {
  return STATUSES.find((s) => s.id === status)?.label ?? status;
}

type TypeFilter = "all" | "custom" | "normal";

function OrderListPage() {
  const [statusFilter, setStatusFilter] = useState<ErpOrderStatus | "all">("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isProduction = user?.role === "production";
  const canMarkReady = isProduction || user?.role === "admin" || user?.role === "manager";

  const ordersQ = useOrders({
    ...(statusFilter === "all" ? {} : { status: statusFilter }),
    ...(typeFilter === "all" ? {} : { custom: typeFilter === "custom" }),
    ...(isProduction && search.trim() ? { q: search.trim() } : {}),
  });

  const markReadyMut = useMutation({
    mutationFn: (id: string) => erpApi.orders.markReady(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["erp", "orders"] });
      toast.success("Marked ready for dispatch");
    },
    onError: () => toast.error("Could not mark ready"),
  });

  const filtered = useMemo(() => {
    const items = ordersQ.data?.items ?? [];
    if (isProduction || !search.trim()) return items;
    const needle = search.trim().toLowerCase();
    return items.filter((o) =>
      [o.code, o.account_name, o.contact_name].some((s) => s?.toLowerCase().includes(needle)),
    );
  }, [ordersQ.data?.items, search, isProduction]);

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ShoppingCart className="size-5 opacity-70" aria-hidden />
            Orders
          </h1>
          <p className="text-sm opacity-60">
            {isProduction
              ? "Production requirements and history. Customer and commercial information is hidden."
              : "Customer orders. Build an order, mark custom items ready when produced, then add it to a dispatch."}
          </p>
        </div>
        {!isProduction && (
          <Link
            to="/erp/orders/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            New order
          </Link>
        )}
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] p-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium opacity-80">
            <Filter className="mr-1 inline size-3" aria-hidden />
            Status
          </span>
          <div className="flex flex-wrap gap-1">
            {STATUSES.filter((s) => !isProduction || s.id !== "draft").map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatusFilter(s.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  statusFilter === s.id
                    ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                    : "border-[var(--border)] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex flex-1 flex-col gap-1">
          <span className="text-xs font-medium opacity-80">Search</span>
          <div className="flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1.5 focus-within:border-[var(--accent)]">
            <Search className="size-4 opacity-50" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isProduction ? "Order or item code/name" : "Code, account, or contact"}
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium opacity-80">Type</span>
          <div className="flex flex-wrap gap-1">
            {(["all", "normal", "custom"] as TypeFilter[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(t)}
                className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  typeFilter === t
                    ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                    : "border-[var(--border)] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {ordersQ.isLoading ? (
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading orders…
        </p>
      ) : ordersQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load orders: {(ordersQ.error as Error).message}
        </p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <ShoppingCart className="mx-auto size-8 opacity-30" aria-hidden />
          <p className="mt-2 text-sm opacity-60">
            {ordersQ.data?.items.length ? "No orders match this filter." : "No orders yet."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
          <table className="w-full min-w-[42rem] text-sm">
            <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wide opacity-60 dark:bg-white/[0.02]">
              <tr>
                <th className="px-4 py-3.5 font-medium">Code</th>
                <th className="px-4 py-3.5 font-medium">Status</th>
                {!isProduction && <th className="hidden px-4 py-3 font-medium md:table-cell">Account / Contact</th>}
                <th className="px-4 py-3.5 text-right font-medium">Lines</th>
                {!isProduction && <th className="hidden px-4 py-3 text-right font-medium md:table-cell">Total</th>}
                <th className="px-4 py-3.5 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-[var(--border)] last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                >
                  <td className="px-4 py-3.5">
                    <Link
                      to="/erp/orders/$orderId"
                      params={{ orderId: o.id }}
                      className="font-mono font-medium text-[var(--accent)] hover:underline"
                    >
                      {o.code}
                    </Link>
                    {o.has_custom && (
                      <span className="ml-2 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                        custom
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span
                      className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${ORDER_STATUS_STYLES[o.status]}`}
                    >
                      {orderStatusLabel(o.status)}
                    </span>
                  </td>
                  {!isProduction && (
                    <td className="hidden px-4 py-3.5 md:table-cell">
                      {o.account_name || o.contact_name || <span className="opacity-40">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-3.5 text-right tabular-nums">{o.line_count}</td>
                  <td className="px-4 py-3.5 text-right tabular-nums">
                    {o.order_total ? `₹${o.order_total}` : <span className="opacity-40">—</span>}
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    {canMarkReady && o.status === "pending_production" ? (
                      <button
                        type="button"
                        onClick={() => markReadyMut.mutate(o.id)}
                        disabled={markReadyMut.isPending}
                        className="inline-flex items-center gap-1 rounded-md border border-blue-400 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60 dark:text-blue-300 dark:hover:bg-blue-950/40"
                      >
                        <PackageCheck className="size-3.5" aria-hidden /> Mark ready
                      </button>
                    ) : (
                      <span className="opacity-30">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
